/**
 * Price in the brand's currency — plain `Intl` with `style: 'currency'`.
 *
 * What this buys over the old manual concatenation
 * (`toLocaleString(...) + ' ₽'`):
 *
 * - in the Russian UI the string looks the same — "1 490 ₽". The one
 *   invisible difference: a non-breaking space before the ruble sign instead
 *   of a regular one, so the price never wraps between number and sign;
 * - in the English UI the ruble renders the English-locale way — "RUB 1,490",
 *   not "1,490 ₽" with a hand-appended sign;
 * - TaleAroo dollars work out of the box: "$19.00", no symbol tables.
 *
 * The UI language arrives as a short tag (`ru`, `en`) — `Intl` expands it
 * to the same locale as the full one (`ru-RU`, `en-US`).
 */
export function formatPrice(minorUnits, brand, displayLocale) {
    const digits = brand.priceFractionDigits;
    return new Intl.NumberFormat(displayLocale ?? brand.priceLocale, {
        style: 'currency',
        currency: brand.currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(minorUnits / 100);
}
/**
 * A list price as a price tag: a whole amount without its zero cents — `$14`, not `$14.00` —
 * the way pricing pages quote it. A charge or a discounted amount keeps every digit
 * (`formatPrice`); this is for prices a buyer compares at a glance.
 */
export function formatListPrice(minorUnits, brand, displayLocale) {
    if (minorUnits % 100 !== 0)
        return formatPrice(minorUnits, brand, displayLocale);
    return new Intl.NumberFormat(displayLocale ?? brand.priceLocale, {
        style: 'currency',
        currency: brand.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(minorUnits / 100);
}
/** Apply a percentage or fixed discount, using the currency display precision. */
export function discountedPrice(listAmountMinorUnits, discountPercent, priceFractionDigits = 2, fixedDiscountMinorUnits = null) {
    const quantum = 10 ** Math.max(0, 2 - priceFractionDigits);
    const discountedAmount = fixedDiscountMinorUnits === null
        ? (listAmountMinorUnits * (100 - discountPercent)) / 100
        : Math.max(0, listAmountMinorUnits - fixedDiscountMinorUnits);
    const amountMinorUnits = Math.round(discountedAmount / quantum) * quantum;
    const discountAmountMinorUnits = listAmountMinorUnits - amountMinorUnits;
    return {
        listAmountMinorUnits,
        discountPercent,
        discountAmountMinorUnits,
        amountMinorUnits,
    };
}
//# sourceMappingURL=price.js.map